import type { FieldDefinition } from '../../../core/assistant/utils/discoverNodeFields';
import type { Node } from '../../../core/models/scene';
import { fieldRegistry } from '../../../core/fields/registry/FieldRegistry';
import { getOrgChartPersonFieldPacks } from '../fields/fieldPackToManifest';
import { normalizeFieldId } from '../fields/import-aliases';
import type {
  OrgCustomField,
  OrgCustomFieldDefinition,
  OrgCustomFieldType,
} from '../models/customFields';

const ORG_PERSON_TYPES = new Set([
  'org_person',
  'org_person_photo',
  'org_person_photo_left',
  'org_person_photo_right',
  'org_person_photo_inner_left',
  'org_person_text',
]);

const INTERNAL_DISCOVERY_SKIP_FIELDS = new Set([
  'customFields',
  'customFieldOverrides',
  'visibleFields',
  'dataBinding',
  'tags',
  'managerId',
  'photoRef',
  'parentDepartmentId',
]);

const hasValue = (value: unknown): boolean =>
  value !== undefined && value !== null && value !== '';

const toCustomFieldKey = (name: string): string => name.trim();

const normalizeCustomFieldName = (name: string): string =>
  name.trim().toLowerCase().replace(/\s+/g, ' ');

export const normalizeFieldRecord = (
  source: Record<string, unknown>,
): Record<string, unknown> => {
  const normalized: Record<string, unknown> = {};
  for (const [rawKey, value] of Object.entries(source)) {
    if (!hasValue(value)) continue;
    const key = normalizeFieldId(rawKey);
    if (!hasValue(normalized[key])) {
      normalized[key] = value;
    }
  }
  return normalized;
};

export const buildOrgAssistantFieldMetadata = (
  baseFields: FieldDefinition[],
): {
  labelOverrides: Record<string, string>;
  skipFields: Set<string>;
} => {
  const baseFieldIds = new Set(baseFields.map((field) => field.id));
  const labelOverrides: Record<string, string> = {};
  const personFieldPackIds = getOrgChartPersonFieldPacks();
  const personFields = fieldRegistry.getFieldsFromPacks(personFieldPackIds);

  for (const field of personFields) {
    labelOverrides[field.id] = field.label;
  }

  return {
    labelOverrides,
    skipFields: new Set([...Array.from(baseFieldIds), ...Array.from(INTERNAL_DISCOVERY_SKIP_FIELDS)]),
  };
};

const mapCustomFieldTypeToConditionType = (
  type: OrgCustomFieldType,
): FieldDefinition['type'] => {
  if (type === 'number') return 'number';
  if (type === 'date') return 'date';
  return 'text';
};

export const buildCustomDefinitionAssistantFields = (
  definitions: OrgCustomFieldDefinition[],
): FieldDefinition[] => {
  const fields: FieldDefinition[] = [];
  const seen = new Set<string>();

  for (const definition of definitions) {
    if (definition.entityType !== 'person') continue;
    const customId = toCustomFieldKey(definition.name);
    if (!customId) continue;
    const dedupeKey = normalizeCustomFieldName(customId);
    if (seen.has(dedupeKey)) continue;
    seen.add(dedupeKey);
    fields.push({
      id: customId,
      label: definition.name,
      type: mapCustomFieldTypeToConditionType(definition.type),
    });
  }

  return fields;
};

export const mergeAssistantBaseFields = (
  baseFields: FieldDefinition[],
  extraFields: FieldDefinition[],
): FieldDefinition[] => {
  const merged = new Map<string, FieldDefinition>();
  for (const field of baseFields) {
    merged.set(field.id, field);
  }
  for (const field of extraFields) {
    if (merged.has(field.id)) continue;
    merged.set(field.id, field);
  }
  return Array.from(merged.values());
};

export const buildOrgAssistantDiscoveryNodes = ({
  nodes,
  customFieldDefinitions,
  resolveCustomFields,
}: {
  nodes: Node[];
  customFieldDefinitions: OrgCustomFieldDefinition[];
  resolveCustomFields: (params: {
    nodeType: string;
    attrs: Record<string, unknown>;
    props: Record<string, unknown>;
    definitions: OrgCustomFieldDefinition[];
  }) => OrgCustomField[];
}): Array<{ id: string; attrs?: Record<string, unknown>; props?: Record<string, unknown> }> =>
  nodes
    .filter((node) => ORG_PERSON_TYPES.has(String(node.type)))
    .map((node) => {
      const attrs = (node.attrs ?? {}) as Record<string, unknown>;
      const props = (node.props ?? {}) as Record<string, unknown>;
      const customFields = resolveCustomFields({
        nodeType: node.type as string,
        attrs,
        props,
        definitions: customFieldDefinitions,
      });

      const normalizedAttrs = normalizeFieldRecord(attrs);
      const normalizedProps = normalizeFieldRecord(props);
      const promoted: Record<string, unknown> = {};
      for (const customField of customFields) {
        if (!customField.name || !hasValue(customField.value)) continue;
        const customKey = toCustomFieldKey(customField.name);
        if (!customKey || hasValue(promoted[customKey])) continue;
        if (!hasValue(promoted[customKey])) {
          promoted[customKey] = customField.value;
        }
      }

      return {
        id: node.id,
        attrs: { ...normalizedAttrs, ...promoted },
        props: normalizedProps,
      };
    });
